/**
 * All landing-page marketing copy in one place. Edit words here, not in the
 * section builders. Web-preview only — this whole folder is disposable UI
 * (`.claude/rules/mobile.md`); Unity ships its own front end.
 */

export const TAGLINE = 'A time-travel survival-horror. One dawn. One way home.';

export interface Feature {
  readonly title: string;
  readonly body: string;
}

export const FEATURES: readonly Feature[] = [
  {
    title: 'The doom clock',
    body: 'A single countdown drives everything — the sun reddens, the fog thickens, the impactor swells. The world visibly ends around you as you work.',
  },
  {
    title: 'You are prey',
    body: 'No weapons. Crouch to break line of sight, move slow to stay quiet, sprint only when the alternative is worse. A wound caps your speed, kills your sprint, and a second hit while you’re bleeding is what actually ends a run.',
  },
  {
    title: 'Four guardians, one each',
    body: 'Dakotaraptor, T. rex, Deinosuchus and Quetzalcoatlus each hold a power core — on land, in the water, in the air. Every one runs the same patrol → suspicious → hunting escalation, fed by sight, hearing and smell. None of them teleport and none of them forget.',
  },
  {
    title: 'The herd isn’t harmless',
    body: 'Triceratops, Ankylosaurus and Edmontosaurus roam the floodplain as ambient life — but crowd one past its warning distance and it charges, swings, or bolts through whatever’s in its way. Scenery that can end your run if you’re careless.',
  },
  {
    title: 'The bio-scanner',
    body: 'The non-violent loop: hold to scan the wildlife, fill the codex, learn the floodplain. Knowing what lives here is how you survive the walk back.',
  },
];

/** One bestiary card. `image` is a `/generated/...` runtime path synced by
 * `tools/sync-runtime-assets.mjs` — see its `RUNTIME_FILES` for what ships. */
export interface BestiaryEntry {
  readonly name: string;
  readonly role: string;
  readonly body: string;
  readonly image: string;
}

export const GUARDIAN_BESTIARY: readonly BestiaryEntry[] = [
  {
    name: 'Dakotaraptor',
    role: 'North core — land',
    body: 'A pack-hunting dromaeosaur that works a patrol, catches a sound or a scent, and commits once it has you. Fast, low, and it does not give up a trail.',
    image: '/generated/dakotaraptor/preview.webp',
  },
  {
    name: 'Tyrannosaurus rex',
    role: 'East core — land, apex',
    body: 'The floodplain’s largest guardian. Slower to notice, unrecoverable once it does — there is no outrunning a full charge in the open.',
    image: '/generated/tyrannosaurus/preview.webp',
  },
  {
    name: 'Deinosuchus',
    role: 'West core — water ambush',
    body: 'Holds the pond and its banks, all but invisible until it isn’t. Crossing open water near its territory is the mistake it’s waiting for.',
    image: '/generated/deinosuchus/preview.webp',
  },
  {
    name: 'Quetzalcoatlus',
    role: 'South core — air, nesting',
    body: 'A giant pterosaur guarding its nest from above. No cover is safe if it has already spotted you from height.',
    image: '/generated/quetzalcoatlus/preview.webp',
  },
];

export const HERD_BESTIARY: readonly BestiaryEntry[] = [
  {
    name: 'Triceratops',
    role: 'Deterrent: charge',
    body: 'A lowered head and a step forward is the only warning you get before three tonnes commit to a charge.',
    image: '/generated/triceratops/source.png',
  },
  {
    name: 'Ankylosaurus',
    role: 'Deterrent: tail swing',
    body: 'Armoured and slow to provoke, but crowd it past its warning distance and the tail club swings without further notice.',
    image: '/generated/ankylosaurus/source.png',
  },
  {
    name: 'Edmontosaurus',
    role: 'Deterrent: none — flees only',
    body: 'A herd animal with no offensive display of its own. It runs from trouble rather than answer it — which does not make the herd itself harmless to be caught inside of.',
    image: '/generated/edmontosaurus/source.jpg',
  },
];

export const FLORA_BESTIARY: readonly BestiaryEntry[] = [
  {
    name: 'Hell Creek Conifer',
    role: 'Canopy — cf. Metasequoia sp.',
    body: 'The dominant canopy tree of the floodplain; its shed needled shoots make up most of the litter underfoot.',
    image: '/generated/flora/conifer/concept.png',
  },
  {
    name: 'Ginkgo',
    role: 'Canopy — one of the survivors',
    body: 'Unchanged for a hundred million years before this morning, and unchanged for sixty-six million more after it.',
    image: '/generated/flora/ginkgo/concept.png',
  },
  {
    name: 'Fan Palm',
    role: 'Canopy — early angiosperm',
    body: 'Broad fronds that scan clearly but move in any wind — a poor place to try to disappear.',
    image: '/generated/flora/palm/concept.png',
  },
  {
    name: 'Tree Fern',
    role: 'Understorey',
    body: 'Marks the wettest, softest ground on the floodplain — worst footing, shortest sightlines, worth remembering.',
    image: '/generated/flora/tree_fern/concept.png',
  },
  {
    name: 'Cycad',
    role: 'Understorey',
    body: 'Slow-growing, long-lived, and comprehensively poisonous in every part that fruits.',
    image: '/generated/flora/cycad/concept.png',
  },
  {
    name: 'Fallen Bole',
    role: 'Deadfall — cover',
    body: 'A downed trunk hollowed by fungus and insects — one of the few places a crouched shape stays hidden from a standing one.',
    image: '/generated/flora/log/concept.png',
  },
];

export interface FooterLink {
  readonly label: string;
  readonly href: string;
  /** External link — opens in a new tab. */
  readonly external?: boolean;
  /** Placeholder with no destination yet. */
  readonly soon?: boolean;
}

export interface FooterColumn {
  readonly heading: string;
  readonly links: readonly FooterLink[];
}

export const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    heading: 'Game',
    links: [
      { label: 'Story', href: '#ls-story' },
      { label: 'Teaser', href: '#ls-trailer' },
      { label: 'About', href: '#ls-about' },
      { label: 'Bestiary', href: '#ls-bestiary' },
      { label: 'Report a bug', href: '#ls-feedback' },
      { label: 'Play now', href: '#play' },
    ],
  },
  {
    heading: 'Get the app',
    links: [
      { label: 'App Store — coming soon', href: '#', soon: true },
      { label: 'Google Play — coming soon', href: '#', soon: true },
    ],
  },
];

export const FOOTER_BLURB = 'One island. Seven species. Free to play, right in your browser.';

export const COPYRIGHT = `© ${new Date().getFullYear()} EXTINCTRUM. All rights reserved.`;
